'use client'

import { Settings2, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useId, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DEFAULT_DOCUMENT_EXPORT_SETTINGS,
  DOCUMENT_EXPORT_FONT_SIZE_OPTIONS,
  type DocumentExportSettings,
  type ExportTextDirection,
} from '@/lib/document-export-settings'

type DocumentExportSettingsButtonProps = {
  settings: DocumentExportSettings
  onChange: (settings: DocumentExportSettings) => void
}

export function DocumentExportSettingsButton({
  settings,
  onChange,
}: DocumentExportSettingsButtonProps) {
  const t = useTranslations('DocumentExportSettingsButton')
  const [open, setOpen] = useState(false)
  const titleId = useId()
  const directionLabels: Record<ExportTextDirection, string> = {
    auto: t('directions.auto'),
    ltr: t('directions.ltr'),
    rtl: t('directions.rtl'),
  }

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open])

  const updateSettings = (next: Partial<DocumentExportSettings>) => {
    onChange({ ...settings, ...next })
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => setOpen(true)}
        aria-label={t('ariaLabel')}
      >
        <Settings2 className="h-4 w-4" aria-hidden />
      </Button>

      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 duration-200 animate-in fade-in zoom-in-95">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative w-full max-w-[380px] rounded-xl border bg-background p-5 shadow-xl"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className="h-4 w-4" aria-hidden />
              <span className="sr-only">{t('close')}</span>
            </button>

            <div className="pr-8">
              <h2 id={titleId} className="text-base font-semibold">
                {t('title')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('description')}
              </p>
            </div>

            <div className="mt-5 space-y-4">
              <label className="block space-y-2">
                <span className="text-sm font-medium">
                  {t('textDirection')}
                </span>
                <Select
                  value={settings.textDirection}
                  onValueChange={(value) =>
                    updateSettings({
                      textDirection: value as ExportTextDirection,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(directionLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <label className="block space-y-2">
                <span className="text-sm font-medium">{t('baseFontSize')}</span>
                <Select
                  value={String(settings.fontSizePt)}
                  onValueChange={(value) =>
                    updateSettings({ fontSizePt: Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_EXPORT_FONT_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {t('fontSizeOption', { size })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>

            <div className="mt-6 flex justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onChange(DEFAULT_DOCUMENT_EXPORT_SETTINGS)}
              >
                {t('reset')}
              </Button>
              <Button type="button" onClick={() => setOpen(false)}>
                {t('done')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
